import { useCallback, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { localStorageService } from "@/services/LocalStorageService";
import type { GapsViewFilterPreference } from "@/utils/models";
import type { GapsDayFilter } from "./gapsDayModel";
import { fromStoredFilter, toStoredFilter } from "./gapsFilterStorage";

export type GapsFilterState = {
	/** The live scope the screen is narrowed to. Sets, because that is what `narrowSections` takes. */
	filter: GapsDayFilter;
	/** True when anything is narrowed — drives the "filtered" note and the chip's active style. */
	isNarrowed: boolean;
	/** Replace the scope. `undefined` for a dimension means "everything". */
	setFilter: (next: GapsDayFilter) => void;
	/** Back to the whole building, and forget any saved scope. */
	clearFilter: () => void;
	/** Whether the current scope is remembered for next time. */
	isSaved: boolean;
	setIsSaved: (next: boolean) => void;
};

const EMPTY_FILTER: GapsDayFilter = {};

/**
 * The gaps-first home's saved scope: which jobs, units and shift windows this user wants to see.
 *
 * Lives in `localStorage` per user + department, like the view preference, because it is a personal
 * working scope rather than a department setting — a supervisor who owns three carts and the scheduler
 * who owns the building look at the same data through different windows, and neither should move the
 * other's. "Save as my default" starts ON, as the artifact draws it: the scope a head nurse sets is the
 * scope they work in, so the screen opens that way next time until they switch it off or tap "Clear
 * filter". Switching it off applies to this visit only — it removes the saved scope, it is not
 * itself remembered.
 *
 * `available` is passed in because only the day's data knows which jobs, locations and windows exist.
 * Only the rows the user actually narrowed are stored as lists; an untouched row is stored as
 * "everything" (see `gapsFilterStorage`), so a unit or window that appears later still shows.
 */
export const useGapsFilter = (available: { jobLabels: string[]; locationNames: string[]; timeLabels: string[] }): GapsFilterState => {
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const userId = userInfo?.id;

	// DEFAULT: the signed-in user loads after the first render (`userInfo` starts empty), and a saved scope is
	// per user — so until there is a user there is no scope to read, and the screen shows the whole building.
	const stored = useMemo<GapsViewFilterPreference | null>(
		() => (userId && departmentId ? localStorageService.getGapsViewFilter(departmentId, userId) : null),
		[departmentId, userId],
	);

	// Seeded from storage once. The screen is the source of truth while it is open; storage is only
	// consulted again on a fresh mount, which is what "my default" means.
	const [live, setLive] = useState<GapsDayFilter | null>(null);
	// DEFAULT: saving starts ON on every mount. The artifact's switch is on by default and it is the design
	// that ships; "off" is a per-visit choice (it deletes the saved scope) rather than a stored preference,
	// because nothing in the design asks for the refusal itself to be remembered.
	const [isSaved, setIsSaved] = useState<boolean>(true);

	const filter = useMemo<GapsDayFilter>(() => {
		if (live !== null) return live;
		if (stored === null) return EMPTY_FILTER;
		return fromStoredFilter(stored, available);
	}, [live, stored, available]);

	const persist = useCallback(
		(next: GapsDayFilter, save: boolean): void => {
			// DEFAULT: nothing is saved before the user has loaded — the key is per user, and there is no user yet to
			// save it for. The live scope still applies to the screen.
			if (!userId || !departmentId) return;
			// "Everything" is not a scope worth storing — dropping the key keeps a later-added unit visible.
			const isEverything = next.jobLabels === undefined && next.locationNames === undefined && next.timeLabels === undefined;
			if (!save || isEverything) {
				localStorageService.removeGapsViewFilter(departmentId, userId);
				return;
			}
			localStorageService.setGapsViewFilter(departmentId, toStoredFilter(next), userId);
		},
		[departmentId, userId],
	);

	const setFilter = useCallback(
		(next: GapsDayFilter): void => {
			setLive(next);
			persist(next, isSaved);
		},
		[persist, isSaved],
	);

	// Clears the scope and forgets the saved one, but leaves the switch alone: "Clear filter" answers
	// "what am I looking at", not "should my next filter be remembered".
	const clearFilter = useCallback((): void => {
		setLive(EMPTY_FILTER);
		persist(EMPTY_FILTER, false);
	}, [persist]);

	const handleSetIsSaved = useCallback(
		(next: boolean): void => {
			setIsSaved(next);
			persist(filter, next);
		},
		[persist, filter],
	);

	return {
		filter,
		isNarrowed: filter.jobLabels !== undefined || filter.locationNames !== undefined || filter.timeLabels !== undefined,
		setFilter,
		clearFilter,
		isSaved,
		setIsSaved: handleSetIsSaved,
	};
};
